import { CalendarClock, ChevronDown, X } from "lucide-react";
import { format } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import { Calendar } from "../../../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../../../components/ui/popover";
import {
  buildSessionDeadlinePreset,
  parseSessionDeadlineLocalValue,
} from "../utils/session-voting-deadline";

const PRESETS = [
  { label: "24h before first option", offsetMinutes: 1440 },
  { label: "2h before first option", offsetMinutes: 120 },
];

function parseLocalValue(value) {
  const match = String(value || "").match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/);
  if (!match) return null;
  const [, year, month, day, hour, minute] = match;
  return {
    date: new Date(Number(year), Number(month) - 1, Number(day)),
    timeValue: `${hour}:${minute}`,
  };
}

function pollToday(timeZone) {
  try {
    const date = formatInTimeZone(new Date(), timeZone, "yyyy-MM-dd");
    const [year, month, day] = date.split("-").map(Number);
    return new Date(year, month - 1, day);
  } catch {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth(), today.getDate());
  }
}

function deadlineSummary(value, timeZone) {
  const deadline = parseSessionDeadlineLocalValue(value, timeZone);
  if (!deadline || !Number.isFinite(deadline.getTime())) return null;
  try {
    return formatInTimeZone(deadline, timeZone, "EEE, MMM d · h:mm a zzz");
  } catch {
    return null;
  }
}

export function SessionVotingDeadlineField({
  value,
  onChange,
  timeZone,
  slots = [],
  error = null,
  warning = null,
  disabled = false,
}) {
  const parsed = parseLocalValue(value);
  const selectedDate = parsed?.date || null;
  const selectedTime = parsed?.timeValue || "18:00";
  const summary = deadlineSummary(value, timeZone);
  const today = pollToday(timeZone);
  const presetValues = PRESETS.map((preset) => ({
    ...preset,
    value: buildSessionDeadlinePreset({
      slots,
      offsetMinutes: preset.offsetMinutes,
      timeZone,
    }),
  }));
  const presetsAvailable = presetValues.some((preset) => preset.value);
  const describedBy = [
    "session-voting-deadline-help",
    error ? "session-voting-deadline-error" : null,
    warning ? "session-voting-deadline-warning" : null,
  ].filter(Boolean).join(" ");

  const updateDate = (date) => {
    if (!date) return;
    onChange(`${format(date, "yyyy-MM-dd")}T${selectedTime}`);
  };

  const updateTime = (time) => {
    if (!selectedDate) return;
    onChange(`${format(selectedDate, "yyyy-MM-dd")}T${time}`);
  };

  return (
    <section
      className="mt-8 rounded-2xl border border-slate-200/70 bg-white p-4 dark:border-slate-700 dark:bg-slate-800/60"
      aria-labelledby="session-voting-deadline-heading"
    >
      <div className="flex items-start gap-3">
        <span className="mt-0.5 rounded-xl bg-blue-50 p-2 text-blue-600 dark:bg-blue-950/50 dark:text-blue-300">
          <CalendarClock className="h-4 w-4" aria-hidden="true" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h3 id="session-voting-deadline-heading" className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                Voting deadline
              </h3>
              <p id="session-voting-deadline-help" className="mt-1 text-xs leading-relaxed text-slate-500 dark:text-slate-400">
                Automatic vote reminders count backward from this explicit deadline. The poll does not finalize automatically.
              </p>
            </div>
            {value ? (
              <button
                type="button"
                onClick={() => onChange("")}
                disabled={disabled}
                className="inline-flex min-h-10 items-center gap-1 rounded-full border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700"
              >
                <X className="h-3.5 w-3.5" aria-hidden="true" />
                Clear
              </button>
            ) : null}
          </div>

          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                disabled={disabled}
                aria-label="Edit voting deadline"
                aria-invalid={Boolean(error)}
                aria-describedby={describedBy}
                className={`mt-4 flex min-h-[56px] w-full items-center justify-between gap-3 rounded-xl border px-3 py-2 text-left transition-colors hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary disabled:opacity-50 dark:hover:bg-slate-800 ${
                  error
                    ? "border-red-300 dark:border-red-700"
                    : value
                      ? "border-blue-200 bg-blue-50/50 dark:border-blue-800 dark:bg-blue-950/20"
                      : "border-dashed border-slate-300 dark:border-slate-600"
                }`}
              >
                <span className="min-w-0">
                  <span className="block text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                    {value ? "Voting closes" : "No deadline set"}
                  </span>
                  <span className="mt-0.5 block truncate text-sm font-semibold text-slate-800 dark:text-slate-100">
                    {summary || "Choose a date and time"}
                  </span>
                  <span className="mt-0.5 block text-[11px] text-slate-500 dark:text-slate-400">
                    {timeZone || "Poll timezone"}
                  </span>
                </span>
                <ChevronDown className="h-4 w-4 shrink-0 text-slate-400" aria-hidden="true" />
              </button>
            </PopoverTrigger>
            <PopoverContent
              align="start"
              className="max-h-[var(--radix-popover-content-available-height)] w-[min(23rem,calc(100vw-2rem))] overflow-y-auto p-3"
              aria-label="Voting deadline editor"
            >
              <div className="space-y-3">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Date</p>
                  <div className="mt-2 flex justify-center rounded-xl border border-border">
                    <Calendar
                      mode="single"
                      selected={selectedDate || undefined}
                      defaultMonth={selectedDate || today}
                      onSelect={updateDate}
                      disabled={(date) => date < today}
                      initialFocus
                    />
                  </div>
                </div>
                <label className="block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Time
                  <input
                    type="time"
                    value={selectedTime}
                    onChange={(event) => updateTime(event.target.value)}
                    disabled={!selectedDate}
                    aria-label="Voting deadline time"
                    className="mt-1 min-h-[42px] w-full rounded-xl border border-input bg-card px-3 text-sm font-normal text-card-foreground focus:outline-none focus:ring-2 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                  />
                </label>
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Quick choices</p>
                  <div className="mt-2 flex flex-wrap gap-2" aria-label="Voting deadline presets">
                    {presetValues.map((preset) => (
                      <button
                        key={preset.offsetMinutes}
                        type="button"
                        onClick={() => onChange(preset.value)}
                        disabled={disabled || !preset.value}
                        title={!preset.value ? "Add a future proposed time far enough away to use this choice." : undefined}
                        className="min-h-10 rounded-full border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-45 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700"
                      >
                        {preset.label}
                      </button>
                    ))}
                  </div>
                  {!presetsAvailable ? (
                    <p className="mt-2 text-[11px] leading-relaxed text-amber-700 dark:text-amber-300">
                      Add a future proposed time to unlock deadline shortcuts.
                    </p>
                  ) : null}
                </div>
              </div>
            </PopoverContent>
          </Popover>

          {error ? (
            <p id="session-voting-deadline-error" role="alert" className="mt-2 text-xs font-semibold text-red-600 dark:text-red-300">
              {error}
            </p>
          ) : warning ? (
            <p id="session-voting-deadline-warning" role="status" className="mt-2 rounded-lg bg-amber-50 px-3 py-2 text-xs font-medium text-amber-800 dark:bg-amber-950/30 dark:text-amber-200">
              {warning}
            </p>
          ) : null}
        </div>
      </div>
    </section>
  );
}
